import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import { ChevronRightIcon, CirclePlay, HomeIcon } from 'lucide-react';

import ContentVideo from 'components/ContentVideo/ContentVideo';
import ContentPodCast from 'components/ContentPodcast/ContentPodcast';
import { Course } from 'types/admin.types';
import { useToggleLessonCompletion } from 'hooks/progress/useToggleLessonCompletion';
import { useAuth } from 'contexts/AuthContext';
import ContentPageSkeleton from 'components/Skeletons/ContentPageSkeleton';
import { useChapterTitle } from 'hooks/course/useChapterTitle';
import { useCourseId } from 'hooks/course/useCourseId';
import LessonComments from 'components/LessonComments/LessonComments';
import { isConversasCourseType } from 'utils/enrollmentAccess';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const ContentPage: React.FC = () => {
  const { user } = useAuth();
  const courseId = useCourseId();
  const [searchParams] = useSearchParams();
  const position = searchParams.get('position');
  const { chapterId, contentType } = useParams<{ chapterId: string; contentType: string }>();
  const [courseData, setCourseData] = useState<Course | null>(null);
  const [content, setContent] = useState<'video' | 'podcast'>('video');
  const [activeLessonId, setActiveLessonId] = useState<string | null>(null);
  const { toggleLessonCompletion, isContentCompleted } = useToggleLessonCompletion();
  const [loading, setLoading] = useState(true); 
  const { chapterTitle } = useChapterTitle(courseId, chapterId);
  const fallbackTitle = Number(position) === 0 ? 'Introdução' : `Capítulo ${position}`;
  const displayTitle = chapterTitle ?? fallbackTitle;

  // Busca curso
  useEffect(() => {
    const fetchCourse = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("accessToken");
        const res = await fetch(`${API_BASE_URL}/api/courses/${courseId}/modules/${chapterId}/lessons`, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const courseData = await res.json();
        setCourseData(courseData);
      } catch (err) {
        console.error("Erro ao buscar course:", err);
       } finally {
        setLoading(false);
      }
    };

    fetchCourse();
  }, [courseId, chapterId]);

    // Capítulo atual
  const chapter = courseData?.response
  const unlockedLessons = useMemo(() => {
    return chapter?.filter((l) => l.isUnlocked) ?? [];
  }, [chapter]);

  // Seleção da aula ativa
  const currentLesson = useMemo(() => {
    return (
      unlockedLessons.find((l) => l.id === activeLessonId) ??
      unlockedLessons.find((l) => l.type === contentType) ??
      null
    );
  }, [unlockedLessons, activeLessonId, contentType]);

  if (!user) return null;

  // Breadcrumb
  const breadcrumbPath = [
    { label: 'Início', path: '/aluna/' },
    { label: displayTitle, path: `/aluna/aulas/capitulo/${chapterId}` },
  ];

    if (loading) {
    return (
      <ContentPageSkeleton/>
    );
  }

  // Renderiza player
  const renderPlayer = () => {
    if (!currentLesson) return null;

    if (content === 'video') {
      return (
        <ContentVideo
          id={currentLesson.id}
          url={currentLesson.url}
          thumbnail={currentLesson.thumbnail || ''}
          title={currentLesson.title}
          description={currentLesson.description ?? ''}
          courseId={courseId ?? ''}
        />
      );
    }

    return (
      <ContentPodCast
        id={currentLesson.id}
        audioUrl={currentLesson.podcast ?? currentLesson.url}
        thumbnail={currentLesson.thumbnail || ''}
        title={currentLesson.title}
        description={currentLesson.description ?? ''}
        toggleLessonCompletion={toggleLessonCompletion}
        isContentCompleted={isContentCompleted}
        duration={currentLesson.duration || ''}
      />
    );
  };

  // Renderiza lista lateral
  const renderLessonList = (type: 'VIDEO' | 'AUDIO') => {
    return (
      <div className="bg-white rounded-xl p-6 shadow-sm">
        <h3 className="font-semibold text-gray-900 mb-4">
          {content === 'video' ? 'Vídeos de Análise' : 'Episódios de Podcast'}
        </h3>
        <div className="space-y-3">
          {unlockedLessons
            .filter((lesson) => lesson.type === type)
            .map((lesson) => {
              const isActive = lesson.id === currentLesson?.id;

              return (
                <button
                  key={lesson.id}
                  onClick={() => setActiveLessonId(lesson.id)}
                  className={`w-full flex items-start gap-3 p-3 rounded-lg transition-all ${isActive ? 'bg-terracota/10 border border-terracota' : 'hover:bg-gray-50'
                    }`}
                >
                  {/* Thumbnail */}
                    <div className="flex-shrink-0 w-24 h-16 rounded-md overflow-hidden relative">
                      <img
                        src={lesson.thumbnail}
                        alt="Thumbnail"
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                        <CirclePlay size={48} color={isActive ? '#7A4330' : '#fafafa91'} />
                      </div>
                    </div>

                  {/* Info */}
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <h4 className={`font-medium ${isActive ? 'text-terracota' : 'text-gray-900'}`}>
                        {lesson.title}
                      </h4>
                      {isContentCompleted(user, lesson.id) && (
                        <span className="text-green-600 text-sm">✓</span>
                      )}
                    </div>
                    <p className="text-sm text-gray-500 mt-1">{lesson.description}</p>
                    {isActive && (
                      <p className="text-xs text-terracota mt-1">
                        {content === 'video' ? 'Assistindo agora' : 'Ouvindo agora'}
                      </p>
                    )}
                  </div>
                </button>
              );
            })}
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Breadcrumb */}
      <div className="mb-6">
        <nav className="flex items-center text-sm text-gray-600">
          <HomeIcon className="mr-1" size={16} />
          {breadcrumbPath.map((item, index) => (
            <React.Fragment key={index}>
              {index > 0 && <ChevronRightIcon className="mx-2" size={16} />}
              {item.path ? (
                <Link to={item.path} className="hover:text-terracota transition-colors">
                  {item.label}
                </Link>
              ) : (
                <span className="font-medium">{item.label}</span>
              )}
            </React.Fragment>
          ))}
        </nav>
      </div>

      {/* Header + Toggle */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 mb-4">
          {currentLesson?.title}
        </h1>
        {contentType !== 'live' && (
          <div className="inline-flex rounded-lg bg-gray-100 p-1">
            <button
              onClick={() => setContent('video')}
              className={`px-4 py-2 rounded-md font-medium transition-all ${content === 'video'
                  ? 'bg-white text-terracota shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              📹 Vídeo
            </button>
            <button
              onClick={() => setContent('podcast')}
              className={`px-4 py-2 rounded-md font-medium transition-all ${content === 'podcast'
                  ? 'bg-white text-terracota shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              🎧 Podcast
            </button>
          </div>
        )}
      </div>

      {/* Main Area */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Player */}
        <div className="lg:col-span-2">{renderPlayer()}</div>

        {/* List */}
        {contentType === 'VIDEO' && renderLessonList('VIDEO')}
        {contentType === 'AUDIO' && renderLessonList('AUDIO')}
      </div>

      {currentLesson && isConversasCourseType(user, courseId ?? '') && (
        <LessonComments lessonId={currentLesson.id} />
      )}
    </div>
  );
};

export default ContentPage;
